import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Universal image lightbox — click an image to expand, X or ESC to close.
 * Preserves the image's natural aspect ratio.
 */
export default function Lightbox({ src, alt = "", onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] flex items-center justify-center p-4 md:p-10 bg-black/95 backdrop-blur-sm"
        onClick={onClose}
        data-testid="lightbox"
      >
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors"
          data-testid="lightbox-close"
          aria-label="Fechar"
        >
          <X className="w-6 h-6" />
        </button>
        <motion.img
          key={src}
          src={src}
          alt={alt}
          title={alt}
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          className="max-w-full max-h-full w-auto h-auto object-contain shadow-2xl"
          data-testid="lightbox-image"
        />
        {alt && (
          <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 text-center text-white/80 text-xs md:text-sm pointer-events-none">
            {alt}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
